import { createMemo, For, Show } from 'solid-js'
import Button from '~/components/Button'
import Icon from '~/components/Icon'
import Input from '~/components/Input'
import ListItemButton from '~/components/ListItemButton'
import ListItemIcon from '~/components/ListItemIcon'
import ListItemText from '~/components/ListItemText'
import type { NodeTypeDTOType } from '~/types/workflow'
import { categoryColor, nodeIcon } from './workflowMeta'

export interface NodePalettePropsType {
  nodeTypes: NodeTypeDTOType[]
  search: string
  onSearch: (value: string) => void
  category: string
  onCategory: (value: string) => void
  onAdd: (type: string) => void
  /** Tipos usados recientemente, para ofrecerlos arriba. */
  recentTypes?: string[]
}

/** Etiqueta legible de una categoría de nodos. */
export const categoryLabel = (category: string): string => {
  switch (category) {
    case 'trigger':
      return 'Disparadores'
    case 'logic':
    case 'control':
      return 'Lógica y control'
    case 'data':
      return 'Datos'
    case 'integration':
      return 'Integraciones'
    case 'ai':
      return 'Inteligencia artificial'
    default:
      return category
  }
}

/**
 * Selector de nodos: búsqueda, categorías con chips y listado agrupado.
 * Se usa igual en el sidebar de escritorio y en el drawer de móvil.
 * Cada nodo es un `ListItemButton` con icono y texto de la librería.
 */
/** Normaliza categorías del backend: `control` se fusiona con `logic`. */
const normalizeCategory = (cat: string): string => (cat === 'control' ? 'logic' : cat)

export function NodePalette(props: NodePalettePropsType) {
  const categories = createMemo(() => [...new Set(props.nodeTypes.map(n => normalizeCategory(n.category)))])

  /** Categorías en orden estable + agrupación de nodos por categoría. */
  const grouped = createMemo(() => {
    const search = props.search.trim().toLocaleLowerCase('es')
    const cat = props.category
    const visible = props.nodeTypes.filter(n => {
      const norm = normalizeCategory(n.category)
      const okCat = cat === 'all' || norm === cat
      const okSearch = !search || `${n.label} ${n.description} ${n.type}`.toLocaleLowerCase('es').includes(search)
      return okCat && okSearch
    })
    const map = new Map<string, NodeTypeDTOType[]>()
    for (const n of visible) {
      const norm = normalizeCategory(n.category)
      const list = map.get(norm) ?? []
      list.push(n)
      map.set(norm, list)
    }
    return [...map.entries()]
  })

  const recentList = createMemo(() => {
    const recent = props.recentTypes ?? []
    if (!recent.length) return []
    return recent
      .map(t => props.nodeTypes.find(n => n.type === t))
      .filter((n): n is NodeTypeDTOType => !!n)
      .slice(0, 5)
  })

  const renderNode = (nt: NodeTypeDTOType) => (
    <ListItemButton class="!rounded-xl" onClick={() => props.onAdd(nt.type)}>
      <ListItemIcon class={categoryColor(nt.category)}>
        <Icon icon={nodeIcon(nt.icon)} size={19} />
      </ListItemIcon>
      <ListItemText primary={nt.label} secondary={nt.description} />
    </ListItemButton>
  )

  return (
    <div class="space-y-3">
      <Input
        placeholder="Buscar nodos…"
        value={props.search}
        onInput={e => props.onSearch((e.currentTarget as HTMLInputElement).value)}
        startIcon="material-symbols-light:search"
        aria-label="Buscar nodos"
      />
      <div class="flex gap-1 overflow-x-auto pb-1" role="group" aria-label="Categorías de nodos">
        <Button size="xs" variant={props.category === 'all' ? 'contained' : 'soft'} onClick={() => props.onCategory('all')}>
          Todos
        </Button>
        <For each={categories()}>
          {c => (
            <Button size="xs" variant={props.category === c ? 'contained' : 'soft'} onClick={() => props.onCategory(c)}>
              {categoryLabel(c)}
            </Button>
          )}
        </For>
      </div>

      {/* Recientes */}
      <Show when={recentList().length && props.category === 'all' && !props.search.trim()}>
        <div>
          <p class="mb-1 text-[11px] font-semibold uppercase tracking-wide text-foreground-subtle">Recientes</p>
          <div class="space-y-1">{recentList().map(renderNode)}</div>
        </div>
        <div class="border-t border-border" />
      </Show>

      {/* Agrupado por categoría */}
      <For each={grouped()}>
        {([cat, nodes]) => (
          <div>
            <p class="mb-1 text-[11px] font-semibold uppercase tracking-wide text-foreground-subtle">{categoryLabel(cat)}</p>
            <div class="space-y-1">{nodes.map(renderNode)}</div>
          </div>
        )}
      </For>

      <Show when={!grouped().length}>
        <p class="rounded-lg bg-surface-container p-3 text-center text-sm text-foreground-subtle">No hay nodos que coincidan.</p>
      </Show>
    </div>
  )
}
